import React, { useState, useEffect, useCallback } from "react";
import { URL, getToken, getLocalDate } from "../utils";
import axios from "axios";
import { toast } from "react-toastify";
import DegustationModal from "./DegustationModal";
import ConditionalRender from "./ConditionalRender";

const ProspectDegustations = ({ prospectId, prospectName, magasinId }) => {
  const [degustations, setDegustations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDegustation, setSelectedDegustation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("view");
  const token = getToken();

  const fetchDegustations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${URL}Prospects/degustation-list-by-prospect/${prospectId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDegustations(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des dégustations:", error);
      toast.error("Erreur lors du chargement des dégustations");
    } finally {
      setLoading(false);
    }
  }, [prospectId, token]);

  useEffect(() => {
    if (prospectId) {
      fetchDegustations();
    }
  }, [prospectId, fetchDegustations]);

  const handleViewDegustation = (degustation) => {
    setSelectedDegustation(degustation);
    setModalType("view");
    setIsModalOpen(true);
  };

  const handleEditDegustation = (degustation) => {
    setSelectedDegustation(degustation);
    setModalType("edit");
    setIsModalOpen(true);
  };

  const handleCreateDegustation = () => {
    // Pre-fill the prospect in the form
    setSelectedDegustation(null);
    setModalType("create");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDegustation(null);
  };

  const handleSaveDegustation = () => {
    fetchDegustations();
    handleCloseModal();
  };

  const renderStars = (note) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-4 h-4 ${
          index < note ? "text-yellow-400" : "text-gray-300"
        }`}
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Dégustations ({degustations.length})
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Historique des dégustations et appréciations
            </p>
          </div>

          <ConditionalRender requiredPermissions="canCreateDegustation">
            <button
              onClick={handleCreateDegustation}
              data-action="create"
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:ring-2 focus:ring-amber-500 text-sm"
            >
              + Nouvelle dégustation
            </button>
          </ConditionalRender>
        </div>

        {degustations.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">
              Aucune dégustation enregistrée
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Commencez par créer une première dégustation
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {degustations.map((degustation) => (
              <div
                key={degustation.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-800">
                        {degustation.product_name || "Produit non défini"}
                      </h3>
                      <div className="flex items-center gap-1">
                        {renderStars(degustation.note || 0)}
                        <span className="text-sm text-gray-600 ml-1">
                          ({degustation.note || 0}/5)
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <QuantityIcon />
                        Quantité: {degustation.quantite || "N/A"}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarIcon />
                        {degustation.date_degustation
                          ? getLocalDate(new Date(degustation.date_degustation))
                          : "Date non définie"}
                      </span>
                    </div>

                    {degustation.commentaire && (
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                        <span className="font-medium">Commentaire:</span>
                        <p className="mt-1">{degustation.commentaire}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <ConditionalRender requiredPermissions="canViewDegustation">
                      <button
                        onClick={() => handleViewDegustation(degustation)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded"
                        title="Voir les détails"
                      >
                        Détails
                      </button>
                    </ConditionalRender>

                    <ConditionalRender requiredPermissions="canEditDegustation">
                      <button
                        onClick={() => handleEditDegustation(degustation)}
                        className="text-amber-600 hover:text-amber-800 text-sm font-medium px-2 py-1 rounded"
                        title="Modifier"
                      >
                        Modifier
                      </button>
                    </ConditionalRender>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <DegustationModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveDegustation}
          degustation={selectedDegustation}
          type={modalType}
          prospectId={prospectId}
          prospectName={prospectName}
          magasinId={magasinId}
          entityType="prospect"
        />
      )}
    </>
  );
};

// Icônes utilitaires
const QuantityIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-2 14h14l-2-14M10 9v6M14 9v6"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
  </svg>
);

export default ProspectDegustations;
